import { Component, Input, Output, EventEmitter } from '@angular/core';
import { EllipsisMenuItem } from '@gainsystems/gains-ui-commons';

/**
 * Interface for breakdown item data
 */
export interface BreakdownItem {
  /** Label for the breakdown item (e.g., "Location MIA") */
  label: string;
  /** Value for the breakdown item */
  value: number;
  /** Formatted value display (e.g., "70,000 $") */
  formattedValue?: string;
}

@Component({
  selector: 'group-breakdown-kpi-widget',
  standalone: false,
  templateUrl: './group-breakdown-kpi-widget.component.html',
  styleUrl: './group-breakdown-kpi-widget.component.scss'
})
export class GroupBreakdownKpiWidgetComponent {
  @Input() widgetTitle = '';
  @Input() totalValue = 0;
  @Input() unit = '';
  @Input() currencySymbol = '$';
  @Input() breakdownItems: BreakdownItem[] = [];
  @Input() widthClass = 'extra-chart-small';
  @Input() menuItems: EllipsisMenuItem[] = [];
  @Input() widgetId = '';
  @Input() hasData = true;
  @Input() maxVisibleItems = 5; // Default maximum visible items
  @Output() changeEvent = new EventEmitter<string>();

  showAllItems = false;

  /**
   * Format number with US format (comma separators)
   */
  formatNumber(value: number): string {
    if (value === null || value === undefined) {
      return '—';
    }
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  /**
   * Get formatted total value with currency/unit
   */
  get formattedTotal(): string {
    if (!this.hasData) {
      return '—';
    }
    return `${this.formatNumber(this.totalValue)} ${this.currencySymbol} total`;
  }

  /**
   * Get breakdown items with formatted values
   */
  get formattedBreakdownItems(): BreakdownItem[] {
    return this.breakdownItems.map(item => ({
      ...item,
      formattedValue: item.formattedValue || `${this.formatNumber(item.value)} ${this.currencySymbol}`
    }));
  }

  /**
   * Handle menu action
   */
  handleMenuAction(action: string): void {
    this.changeEvent.emit(action);
  }

  /**
   * Validate that breakdown values sum to total
   */
  get isDataValid(): boolean {
    if (!this.hasData || this.breakdownItems.length === 0) {
      return true;
    }
    const sum = this.breakdownItems.reduce((acc, item) => acc + item.value, 0);
    // Allow small floating point differences
    return Math.abs(sum - this.totalValue) < 0.01;
  }
}
